/**
 * Compare Layout Strategy
 *
 * Compare mode displays media side-by-side in columns, allowing users to compare
 * different runs, steps, or indices across columns.
 */

import {type MediaCardType} from '@wandb/weave/common/types/media';

import {
  type ColumnConfig,
  type CompareSettings,
  type FanOutVariable,
  type MediaBrowserPanelConfig,
  type TileControls,
  type TileControlType,
  type TileControlValue,
} from '../common';
import {type StepMeta} from '../types';
import {pickSliderStepsOrSteps, resolveCurrentStep} from '../utils/stepUtils';
import {type LayoutStrategy} from './LayoutStrategy';
import {
  type ControlData,
  type ControlDefinition,
  type GridCell,
  type GridData,
  type GridRow,
  type LayoutSlot,
  type RowData,
  type SlotRegistry,
  type TransformInput,
} from './types';
import {getAllAxisValues} from './utils/axisValues';
import {createCell, createEmptyCell} from './utils/cells';
import {createEmptySlotRegistry} from './utils/slotUtils';

const DEBUG = false;
const log = DEBUG ? console.log : () => {};

// ============================================================================
// Helper Functions for Slot Registry Logic
// ============================================================================

/**
 * Control placement configuration for a dimension.
 */
type ControlPlacement = {
  control: ControlDefinition;
  slot: LayoutSlot;
};

function getControlPlacement(
  dimension: TileControlType,
  tileControlValue: TileControlValue | undefined,
  fanOutVariable: FanOutVariable | undefined,
  isFullScreen: boolean
): ControlPlacement | null {
  // Hide control if explicitly set to 'hidden'
  if (tileControlValue === 'hidden') {
    return null;
  }

  // If this dimension is being fanned out, place it in row-header as a label
  // (mediaKey cannot be fanned out, so skip this check for it)
  if (fanOutVariable && dimension === fanOutVariable) {
    const control: ControlDefinition = {
      control: dimension,
      type: 'label',
    };
    return {control, slot: 'rowHeader'};
  }

  // Default to 'linked' if not specified
  const controlMode = tileControlValue ?? 'linked';

  // Determine which control to use - define inline for clarity
  const control: ControlDefinition = {
    control: dimension,
    type: isFullScreen ? 'component' : 'label',
  };

  if (dimension === 'run') {
    control.variant = isFullScreen ? 'card' : 'standard';
    control.labelPosition = isFullScreen ? 'separated' : 'value';
  } else if (dimension === 'mediaKey') {
    control.variant = isFullScreen ? 'card' : 'standard';
    control.labelPosition = isFullScreen ? 'separated' : 'inline';
    // Always use single select - each column has one media key
    // In 'linked' mode, all columns share the same single media key
    // In 'individual' mode, each column has its own media key
    control.selectionMode = 'single';
  } else if (dimension === 'step' || dimension === 'index') {
    control.variant = isFullScreen ? 'card' : 'standard';
    control.labelPosition = isFullScreen ? 'separated' : 'inline';
  }

  const slot: LayoutSlot =
    controlMode === 'individual' ? 'columnHeader' : 'contentFooter';

  return {control, slot};
}

// ============================================================================
// Helper Functions for Transform Logic
// ============================================================================

/**
 * Determines which media keys belong to a specific column.
 *
 * @param tileControls - Tile control configuration
 * @param columnConfig - Column-specific configuration (may be undefined)
 * @param globalMediaKeys - Global media keys from config
 * @returns Array of media keys for this column
 */
function determineColumnMediaKeys(
  tileControls: {mediaKey?: string},
  columnConfig: {mediaKey?: string | string[]} | undefined,
  globalMediaKeys: string[] | undefined
): string[] {
  if (tileControls.mediaKey === 'individual' && columnConfig?.mediaKey) {
    return Array.isArray(columnConfig.mediaKey)
      ? columnConfig.mediaKey
      : [columnConfig.mediaKey];
  }
  return globalMediaKeys || [];
}

/**
 * Determines which run a column should display.
 *
 * @param tileControls - Tile control configuration
 * @param columnConfig - Column-specific configuration (may be undefined)
 * @param sharedRunName - Shared run name from compareSettings
 * @param runs - Available runs
 * @returns The run to display in this column, or undefined if not found
 */

function determineColumnRun(
  tileControls: {run?: string},
  columnConfig: {run?: string} | undefined,
  sharedRunName: string | undefined,
  runs: TransformInput['runs']
): TransformInput['runs'][0] | undefined {
  // Determine which run name to use
  let runName: string | undefined;

  if (tileControls.run === 'individual' && columnConfig?.run) {
    // Use column-specific run when in individual mode
    runName = columnConfig.run;
  } else if (tileControls.run === 'linked' && sharedRunName) {
    // Use shared run name when in linked mode
    runName = sharedRunName;
  }

  // Find the run by name, fallback to first run if not found
  // With skipFiltering enabled, all configured runs should be available
  // Fallback ensures we create a cell (not empty cell) so TileMediaLoader can show errors
  return runs.find(r => runName && r.name === runName) ?? runs[0];
}

/**
 * Determines which index a column should display.
 *
 * @param tileControls - Tile control configuration
 * @param columnConfig - Column-specific configuration (may be undefined)
 * @param currentIndex - Current index from input (for linked mode)
 * @returns The index value to use for this column
 */
function determineColumnIndex(
  tileControls: {index?: string},
  columnConfig: {index?: number} | undefined,
  currentIndex: number | undefined
): number {
  if (tileControls.index === 'individual' && columnConfig?.index != null) {
    return columnConfig.index;
  }
  return currentIndex ?? 0;
}

/**
 * Determines the fan-out values for compare mode.
 *
 * Fan-out values represent the dimension being iterated over (e.g., multiple steps or indices).
 * For non-fanout mode, returns a single placeholder value [0].
 *
 * When fanning out on step, values are filtered to only include steps where at least
 * one of the column-selected runs actually logged data, avoiding empty rows from
 * runs not visible in the panel.
 *
 * @param fanOutVariable - The dimension to fan out on (step, index, run, mediaKey, or undefined)
 * @param runs - Available runs
 * @param stepMeta - Step metadata
 * @param currentStep - Current step value (for fallback)
 * @param currentIndex - Current index value (for fallback)
 * @param mediaSliderKey - Key for step slider
 * @param compareSettings - Compare settings with column configs
 * @param columnCount - Number of columns
 * @returns Array of fan-out values
 */
function determineFanOutValues(
  fanOutVariable: FanOutVariable | undefined,
  runs: TransformInput['runs'],
  stepMeta: StepMeta,
  currentStep: number | undefined,
  currentIndex: number | undefined,
  mediaSliderKey: string,
  compareSettings: CompareSettings | undefined,
  columnCount: number
): number[] {
  if (!fanOutVariable) {
    // Standard compare mode (no fan-out): return a single placeholder value
    // to make the loop run once. The value itself (0) is never used - when
    // fanOutVariable is undefined, all conditionals fall through to use
    // column config or current step/index instead.
    return [0];
  }

  let fanOutValues = getAllAxisValues(fanOutVariable, runs, stepMeta);

  if (fanOutVariable === 'step' && fanOutValues.length > 0) {
    const columnRunNames = getColumnRunNames(
      compareSettings,
      columnCount,
      runs
    );
    if (columnRunNames.length > 0) {
      // StepMetaRun.id is the run name, matching compareSettings.columns[].run
      const columnRunStepSets = stepMeta.runs
        .filter(r => columnRunNames.includes(r.id))
        .map(r => r.sliderStepSet ?? r.stepSet);
      if (columnRunStepSets.length > 0) {
        fanOutValues = fanOutValues.filter(step =>
          columnRunStepSets.some(set => set.has(step))
        );
      }
    }
  }

  // Apply fallbacks only for index/step when empty.
  // Run doesn't get a fallback because if there are no runs, there's nothing to compare.
  // Returning empty fanOutValues will result in an empty grid, which is correct.
  if (fanOutValues.length === 0) {
    if (fanOutVariable === 'index') {
      fanOutValues = [currentIndex ?? 0];
    } else if (fanOutVariable === 'step') {
      fanOutValues = [
        resolveCurrentStep(currentStep, stepMeta, mediaSliderKey),
      ];
    }
    // Note: 'run' and 'mediaKey' don't have fallbacks - empty array means no data to display
  }

  return fanOutValues;
}

/**
 * Collects the unique run names selected across all compare columns.
 */
function getColumnRunNames(
  compareSettings: CompareSettings | undefined,
  columnCount: number,
  runs: TransformInput['runs']
): string[] {
  const columns = (compareSettings?.columns ?? []).slice(0, columnCount);
  const tileControls = compareSettings?.tileControls ?? {};
  const names = new Set<string>();

  if (tileControls.run === 'individual') {
    for (const col of columns) {
      if (col.run) {
        names.add(col.run);
      }
    }
  } else if (compareSettings?.runName) {
    names.add(compareSettings.runName);
  }

  // If no explicit run config, fall back to first run (the default behavior)
  if (names.size === 0 && runs.length > 0) {
    names.add(runs[0].name);
  }

  return Array.from(names);
}

/**
 * Configuration for a single column at a specific fan-out value.
 *
 * This represents all the data needed to create cells for one column:
 * which run, step, index, and media keys to display.
 */
type ColumnSpec = {
  columnIndex: number;
  run: TransformInput['runs'][0] | undefined;
  step: number;
  index: number;
  mediaKeys: string[];
};

/**
 * Creates rows for the grid by iterating [rows][columns].
 *
 * For each row index (up to max media keys across all columns):
 * - For each column: create cell at that row index
 * - Pad with empty cells if column has fewer media keys
 *
 * This direct approach works because we can always determine the row count upfront:
 * `max(column_0.mediaKeys.length, column_1.mediaKeys.length, ...)`
 *
 * @param params - Parameters for creating rows
 * @returns Array of grid rows
 */
function createRows(params: {
  fanOutVariable: FanOutVariable | undefined;
  fanOutValue: number;
  fanOutIdx: number;
  columnCount: number;
  columns: ColumnConfig[];
  tileControls: TileControls;
  mediaKeys: string[];
  runs: TransformInput['runs'];
  stepMeta: StepMeta;
  currentStep: number | undefined;
  currentIndex: number | undefined;
  mediaSliderKey: string;
  compareSettings: CompareSettings | undefined;
  config: MediaBrowserPanelConfig;
  allMasks: TransformInput['allMasks'];
  mediaType: MediaCardType;
}): GridRow[] {
  const {
    fanOutVariable,
    fanOutValue,
    fanOutIdx,
    columnCount,
    columns,
    tileControls,
    mediaKeys,
    runs,
    stepMeta,
    currentStep,
    currentIndex,
    mediaSliderKey,
    compareSettings,
    config,
    allMasks,
    mediaType,
  } = params;

  // Determine column specs for all columns
  const columnSpecs: ColumnSpec[] = [];
  for (let colIdx = 0; colIdx < columnCount; colIdx++) {
    const columnConfig = columns[colIdx];
    const spec = determineColumnSpec({
      columnIndex: colIdx,
      columnConfig,
      fanOutVariable,
      fanOutValue,
      tileControls,
      mediaKeys,
      runs,
      stepMeta,
      currentStep,
      currentIndex,
      mediaSliderKey,
      compareSettings,
    });
    columnSpecs.push(spec);
  }

  // Determine row count: max media keys across all columns
  // (columns may have different counts if using individual media key mode)
  const rowCount = Math.max(
    ...columnSpecs.map(spec => spec.mediaKeys.length),
    0
  );
  const rows: GridRow[] = [];

  // Create rows directly
  for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
    const rowCells: GridCell[] = [];

    // For each column, create the cell at this row index
    for (const columnSpec of columnSpecs) {
      // Get media key for this row (may be undefined if column has fewer keys)
      const mediaKey =
        columnSpec.mediaKeys[rowIdx % columnSpec.mediaKeys.length] ||
        columnSpec.mediaKeys[0] ||
        mediaKeys[0] ||
        'placeholder';

      log(
        `[createRows] Row ${rowIdx}, Column ${columnSpec.columnIndex}: mediaKey="${mediaKey}", columnSpec.mediaKeys=`,
        columnSpec.mediaKeys,
        `step=${columnSpec.step}`
      );

      // If this row index exceeds this column's media keys, pad with empty cell
      if (rowIdx >= columnSpec.mediaKeys.length) {
        rowCells.push(createEmptyCell(mediaKey));
        continue;
      }

      if (!columnSpec.run || !mediaKey) {
        rowCells.push(createEmptyCell(mediaKey));
      } else {
        const runStepMeta = stepMeta.runs.find(
          r => r.id === columnSpec.run!.name
        );
        if (!runStepMeta || !mediaType) {
          rowCells.push(createEmptyCell(mediaKey));
        } else {
          const cell = createCell(
            columnSpec.run,
            runStepMeta,
            columnSpec.step,
            columnSpec.index,
            mediaKey,
            config,
            allMasks,
            mediaType
          );
          if (cell) {
            log(
              `[createRows] Created cell for row ${rowIdx}, col ${columnSpec.columnIndex}: mediaKey="${cell.mediaKey}", step=${cell.step}`
            );
          } else {
            log(
              `[createRows] createCell returned null for row ${rowIdx}, col ${columnSpec.columnIndex}: mediaKey="${mediaKey}", step=${columnSpec.step}`
            );
          }
          rowCells.push(cell || createEmptyCell(mediaKey));
        }
      }
    }

    // Build row key
    const rowKey = fanOutVariable
      ? `${fanOutVariable}-${fanOutValue}-row-${fanOutIdx}-${rowIdx}`
      : `compare-row-${rowIdx}`;

    rows.push({
      cells: rowCells,
      rowKey,
    });
  }

  return rows;
}

/**
 * Determines the configuration (run, step, index, media keys) for a single column.
 *
 * This function encapsulates all the logic for determining what a column should display,
 * separated from the actual cell creation.
 *
 * @param params - Parameters for determining column spec
 * @returns Column specification
 */
function determineColumnSpec(params: {
  columnIndex: number;
  columnConfig: ColumnConfig;
  fanOutVariable: FanOutVariable | undefined;
  fanOutValue: number;
  tileControls: TileControls;
  mediaKeys: string[];
  runs: TransformInput['runs'];
  stepMeta: StepMeta;
  currentStep: number | undefined;
  currentIndex: number | undefined;
  mediaSliderKey: string;
  compareSettings: CompareSettings | undefined;
}): ColumnSpec {
  const {
    columnIndex,
    columnConfig,
    fanOutVariable,
    fanOutValue,
    tileControls,
    mediaKeys,
    runs,
    stepMeta,
    currentStep,
    currentIndex,
    mediaSliderKey,
    compareSettings,
  } = params;

  // Determine media keys for this column
  const columnMediaKeys = determineColumnMediaKeys(
    tileControls,
    columnConfig,
    mediaKeys
  );

  // Determine run for this column
  let run: TransformInput['runs'][0] | undefined;
  if (fanOutVariable === 'run') {
    run = runs[fanOutValue];
  } else {
    run = determineColumnRun(
      tileControls,
      columnConfig,
      compareSettings?.runName,
      runs
    );
  }

  // Determine step for this column
  let step: number;
  if (fanOutVariable === 'step') {
    step = fanOutValue;
  } else {
    step =
      columnConfig?.step ??
      resolveCurrentStep(currentStep, stepMeta, mediaSliderKey);
  }

  // Determine index for this column
  let index: number;
  if (fanOutVariable === 'index') {
    index = fanOutValue;
  } else {
    index = determineColumnIndex(tileControls, columnConfig, currentIndex);
  }

  return {
    columnIndex,
    run,
    step,
    index,
    mediaKeys: columnMediaKeys,
  };
}

/**
 * Builds grid metadata for compare layout.
 *
 * @param rows - All grid rows
 * @param columnCount - Number of columns
 * @param fanOutVariable - The dimension being fanned out
 * @param fanOutValues - Array of fan-out values
 * @returns Grid metadata object
 */
function buildCompareMetadata(
  rows: GridRow[],
  columnCount: number,
  fanOutVariable: string | undefined,
  fanOutValues: number[]
): GridData['metadata'] {
  const xAxisValues = Array.from({length: columnCount}, (_, i) => i);
  const yAxisValues = fanOutVariable ? fanOutValues : [0];
  const totalCells = rows.reduce((sum, row) => sum + row.cells.length, 0);

  return {
    layoutType: 'compare',
    totalCells,
    axisData: {
      x: xAxisValues,
      y: yAxisValues,
    },
    ...(fanOutVariable && {
      yAxisTicks: fanOutValues,
    }),
  };
}

export const CompareLayout: LayoutStrategy = {
  id: 'compare',

  transform(input: TransformInput): GridData {
    /**
     * Transforms input data into a GridData structure for compare mode.
     *
     * Compare mode displays columns side-by-side for comparison.
     * When fanOutVariable is set (e.g., 'index'), creates one set of rows per fanned-out value.
     *
     * This function is organized into 3 testable stages:
     * 1. Determine fan-out values (what dimension to iterate over)
     * 2. For each fan-out value: Build grid rows by iterating [rows][columns]
     * 3. Build metadata
     */

    const {
      runs,
      stepMeta,
      currentStep,
      currentIndex,
      config,
      allMasks,
      mediaType,
    } = input;
    const {compareSettings, mediaKeys} = config;
    const mediaSliderKey = config.mediaSliderKey ?? '_step';

    // Early exit: if no media keys, return empty grid
    if (!mediaKeys || mediaKeys.length === 0 || !mediaType) {
      return {rows: [], metadata: {layoutType: 'compare', totalCells: 0}};
    }

    // Extract configuration with defaults
    const tileControls = compareSettings?.tileControls ?? {};
    const columnCount = config.columnCount ?? 2;
    const columns = (compareSettings?.columns ?? []).slice(0, columnCount);
    const fanOutVariable = compareSettings?.fanOutVariable;

    log('[CompareLayout] Transform:', {
      columnCount,
      columnsLength: columns.length,
      tileControls,
      fanOutVariable,
      runsCount: runs.length,
      mediaKeysCount: mediaKeys.length,
    });

    // Stage 1: Determine fan-out values
    const fanOutValues = determineFanOutValues(
      fanOutVariable,
      runs,
      stepMeta,
      currentStep,
      currentIndex,
      mediaSliderKey,
      compareSettings,
      columnCount
    );

    // Stage 2: For each fan-out value, build rows directly
    const rows: GridRow[] = [];

    for (let fanOutIdx = 0; fanOutIdx < fanOutValues.length; fanOutIdx++) {
      const fanOutValue = fanOutValues[fanOutIdx];

      // Depending on the number of media keys, this may return multiple rows
      const fanOutRows = createRows({
        fanOutVariable,
        fanOutValue,
        fanOutIdx,
        columnCount,
        columns,
        tileControls,
        mediaKeys,
        runs,
        stepMeta,
        currentStep,
        currentIndex,
        mediaSliderKey,
        compareSettings,
        config,
        allMasks,
        mediaType,
      });
      rows.push(...fanOutRows);
    }

    // Filter out rows where every cell is empty (only when fanning out,
    // since fan-out can produce rows beyond what selected runs logged)
    const filteredRows = fanOutVariable
      ? rows.filter(row => row.cells.some(cell => !cell.isEmpty))
      : rows;

    // Stage 3: Build metadata
    const metadata = buildCompareMetadata(
      filteredRows,
      columnCount,
      fanOutVariable,
      fanOutValues
    );

    log('[CompareLayout] Returning grid data:', {
      rowsCount: filteredRows.length,
      cellsPerRow: filteredRows[0]?.cells.length ?? 0,
      columnCount,
      fanOutVariable,
      fanOutValues: fanOutVariable ? fanOutValues : undefined,
      totalCells: metadata.totalCells,
    });

    return {rows: filteredRows, metadata};
  },

  buildSlotRegistry(
    config: MediaBrowserPanelConfig,
    stepMeta?: TransformInput['stepMeta'],
    isFullScreen: boolean = false,
    _isSingleRunView?: boolean
  ): SlotRegistry {
    /**
     * This function determines WHERE controls (dropdowns, sliders, labels) should appear
     * in the UI based on the tileControls configuration.
     *
     * IMPORTANT: Order matters! Controls are pushed in a specific order that affects
     * their visual appearance in the UI.
     *
     * NOTE: When fanOutVariable is set, the control for that dimension
     * is placed in row-header instead of column-header/content-footer.
     */

    const {compareSettings} = config;
    const tileControls = compareSettings?.tileControls ?? {};
    const fanOutVariable = compareSettings?.fanOutVariable;

    log('[CompareLayout] Building slot registry:', {
      tileControls,
      hasCompareSettings: !!compareSettings,
    });

    /**
     * Initialize slot registry with all possible locations
     */
    const slots = createEmptySlotRegistry();

    /**
     * PAGINATION
     *
     * When fanOutVariable is set and will create many rows, add pagination control
     * to contentHeader. The actual pagination threshold and logic is handled by
     * useTilePagination hook.
     */
    if (fanOutVariable) {
      slots.contentHeader = [
        {
          control: 'pagination',
          type: 'component',
        },
      ];
    }

    /**
     * Control values
     *
     * individual: Each column can show a different value
     * linked: All columns show the same value
     * hidden: Don't show control at all
     *
     * NOTE: When fanOutVariable is set, the control for that dimension
     * is placed in row-header instead of column-header/content-footer.
     *
     * NOTE: Order matters for pushing controls into the list.
     * Controls are rendered in the order they appear in each array.
     */
    const dimensions: Array<{
      name: TileControlType;
      tileControlValue: TileControlValue | undefined;
    }> = [
      {name: 'run', tileControlValue: tileControls.run},
      {name: 'mediaKey', tileControlValue: tileControls.mediaKey},
      {name: 'step', tileControlValue: tileControls.step},
      {name: 'index', tileControlValue: tileControls.index},
    ];

    // Determine control placement for each dimension and push directly into slots
    // Track which controls we've already added to prevent duplicates
    const addedControls = new Set<string>();
    for (const {name, tileControlValue} of dimensions) {
      const placement = getControlPlacement(
        name,
        tileControlValue,
        fanOutVariable,
        isFullScreen
      );

      if (placement) {
        // Create a unique key for this control to prevent duplicates
        const controlKey = `${placement.slot}-${placement.control.control}`;
        if (!addedControls.has(controlKey)) {
          addedControls.add(controlKey);
          slots[placement.slot]!.push(placement.control);
        }
      }
    }

    /**
     * CAPTIONS
     *
     * For now, always added to tile-footer.
     */
    const captionSlot =
      config.mode === 'compare' && !config.compareSettings?.fanOutVariable
        ? 'columnFooter'
        : 'tileFooter';
    slots[captionSlot] = [
      {
        control: 'caption',
        type: 'label',
      },
    ];

    return slots;
  },

  buildRowData(input: TransformInput, gridData: GridData): RowData[] {
    const {config, runs} = input;
    const fanOutVariable = config.compareSettings?.fanOutVariable;
    const yAxisTicks = gridData.metadata.yAxisTicks ?? [];
    const rowCount = gridData.rows.length;

    // Multiple linked media keys expand each fan-out value into N visual rows
    // (one per key). We need to map visual row index back to fan-out value index.
    const mediaKeyControl = config.compareSettings?.tileControls?.mediaKey;
    const isLinkedMediaKeys = mediaKeyControl !== 'individual';
    const mediaKeyCount = isLinkedMediaKeys
      ? (config.mediaKeys?.length ?? 1)
      : 1;
    const pageStart = config.page?.start ?? 0;
    const rowOffset = Math.floor(pageStart / (config.columnCount ?? 1));

    return Array.from({length: rowCount}, (_, i) => {
      const fanOutIndex = Math.floor((i + rowOffset) / mediaKeyCount);
      const tick = yAxisTicks[fanOutIndex];

      if (fanOutVariable === 'step') {
        return {step: tick};
      } else if (fanOutVariable === 'run') {
        return {run: tick != null ? runs[tick]?.name : undefined};
      } else if (fanOutVariable === 'index') {
        return {index: tick};
      }
      return {};
    });
  },

  buildControlData(input: TransformInput, _gridData: GridData): ControlData[] {
    const {config, currentStep, runs, stepMeta} = input;
    const columnCount = config.columnCount ?? 2;
    const columns = (config.compareSettings?.columns ?? []).slice(
      0,
      columnCount
    );
    const sharedRunName = config.compareSettings?.runName;
    const mediaKeys = config.mediaKeys ?? [];
    const numSharedMediaKeys = mediaKeys.length;
    const tileControls = config.compareSettings?.tileControls ?? {};
    const isIndividualRun = tileControls.run === 'individual';
    const isIndividualMediaKey = tileControls.mediaKey === 'individual';

    return Array.from({length: columnCount}, (_, i) => {
      const keyIndex =
        numSharedMediaKeys > 1 ? Math.min(i, numSharedMediaKeys - 1) : 0;
      // In individual mode each column can have its own key; in linked mode
      // all columns share the global key regardless of column config.
      const mediaKey = isIndividualMediaKey
        ? (columns[i]?.mediaKey ?? mediaKeys[keyIndex])
        : mediaKeys[keyIndex];

      // In individual mode each column can have its own run; in linked mode
      // (or when unset) all columns share the configured run.
      const run = isIndividualRun
        ? (columns[i]?.run ?? sharedRunName ?? runs[0]?.name)
        : (sharedRunName ?? runs[0]?.name);

      const step = columns[i]?.step ?? currentStep;

      // availableSteps: key-filtered steps for this column's run, falling back
      // to the run's full step list.
      let availableSteps: number[] | undefined;
      if (stepMeta && run) {
        if (mediaKey) {
          const keySteps = stepMeta.stepsByRunKey.get(run)?.get(mediaKey);
          if (keySteps && keySteps.length > 0) {
            availableSteps = keySteps;
          }
        }
        if (availableSteps === undefined) {
          const runMeta = stepMeta.runs.find(r => r.id === run);
          if (runMeta) {
            availableSteps = pickSliderStepsOrSteps(
              runMeta.sliderSteps,
              runMeta.steps
            );
          }
        }
      }

      // availableIndices: step+key-specific count for this column, falling back
      // to the run's full index list.
      let availableIndices: number[] | undefined;
      if (stepMeta && run && mediaKey) {
        const mediaCount = stepMeta.mediaCountByRunKeyStep
          .get(run)
          ?.get(mediaKey)
          ?.get(step);
        if (mediaCount != null && mediaCount > 0) {
          availableIndices = Array.from({length: mediaCount}, (_, idx) => idx);
        } else {
          const runMeta = stepMeta.runs.find(r => r.id === run);
          if (runMeta && runMeta.indices.length > 0) {
            availableIndices = runMeta.indices;
          }
        }
      }

      return {
        step,
        index: columns[i]?.index,
        run,
        mediaKey,
        availableSteps,
        availableIndices,
      };
    });
  },
};
